import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ColumnsMenu from '@ubeya/shared-web/components/Table/ColumnsMenu';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useVisibleColumn from '@ubeya/shared-web/hooks/Table/useVisibleColumns';
import useToaster from '@ubeya/shared/hooks/useToaster';

const ExportableColumns = ({
  enable = false,
  defaultColumns,
  optionalColumns,
  extraData = undefined,
  onClose: inputOnClose,
  renderColumnsMenuExtraData,
  children,
  onRender,
  storageName,
  size = 'small'
}) => {
  const { t } = useContext(ConfigContext);
  const [isAskingColumns, setIsAskingColumns] = useState(enable);

  const { addResponseError } = useToaster();

  const allColumns = useMemo(
    () =>
      defaultColumns.map((column) => ({
        ...column,
        category: column.category || ' ',
        visible: !(optionalColumns || []).includes(column.dataKey)
      })),

    [defaultColumns, optionalColumns]
  );

  const { visibleColumns, updateVisibleColumns, setIsMenuOpen } = useVisibleColumn(allColumns, storageName);

  const onClose = useCallback(() => {
    setIsMenuOpen(false);
    inputOnClose?.();
  }, [inputOnClose, setIsMenuOpen]);

  const uponUpdateVisibleColumns = useCallback(
    (selectedColumns) => {
      setIsAskingColumns(false);
      updateVisibleColumns(selectedColumns);
    },
    [updateVisibleColumns]
  );

  useEffect(() => {
    if (!isAskingColumns && !!onRender) {
      const executeOnRender = async () => {
        try {
          // Wrap onRender call with Promise.resolve to handle both async and sync cases
          await Promise.resolve(onRender(visibleColumns, extraData));
        } catch (error) {
          addResponseError(error);
        }
      };

      executeOnRender();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAskingColumns]);

  if (isAskingColumns) {
    return (
      <ColumnsMenu
        columnsMenuTitle={t('chooseExportableColumns')}
        allColumns={allColumns}
        visibleColumns={visibleColumns}
        onClose={onClose}
        updateVisibleColumns={uponUpdateVisibleColumns}
        showReset={false}
        size={size}
        confirmText="export"
        renderColumnsMenuExtraData={renderColumnsMenuExtraData}
      />
    );
  }

  return children ? children({ columns: visibleColumns }) : <></>;
};

export default ExportableColumns;
